<template>
  <div class="flex gap-[20px] px-[20px] lg:px-0">
    <BaseTabButton
      v-for="(option, index) in tabButtonData"
      :key="index"
      :is-active="option.value === activeTab.value"
      :option="option"
      @change="(val) => (activeTab = val)" />
  </div>
  <div class="md:h-[300px]">
    <IncomeProjectionChart
      v-show="activeTab.value === 'income'"
      class="mt-[20px]"
      :data="incomeChartData"
      :raw="incomeChartRaw"
      :user-metadata="userMetadata"
      :desired-retirement-salary="desiredRetirementIncome"
      :x-axis="chartLabels"
      :loading="loading"
      :hide-explain-button="true"
      :is-retirement-calculator="true" />
    <AssetsProjectionChart
      v-show="activeTab.value === 'assets'"
      class="mt-[20px]"
      :data="assetChartData"
      :raw="assetChartRaw"
      :user-metadata="userMetadata"
      :desired-retirement-salary="desiredRetirementIncome"
      :x-axis="chartLabels"
      :loading="loading"
      :hide-explain-button="true"
      :is-retirement-calculator="true" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import BaseTabButton from '@/components/Inputs/BaseTabButton.vue'
import IncomeProjectionChart from '@/components/ChartJS/IncomeProjectionChart.vue'
import AssetsProjectionChart from '@/components/ChartJS/AssetsProjectionChart.vue'
import { useRetirementCalculatorStore } from '@/store/pinia/RetirementCalculatorStore'
import { useRetirementCalculatorProcessor } from '@/store/pinia/RetirementCalculator/calculatorDataProcessor'

// Store setup
const retirementCalculatorStore = useRetirementCalculatorStore()
const chartProcessor = useRetirementCalculatorProcessor()

const tabButtonData = ref([
  { label: 'Income', value: 'income' },
  { label: 'Assets', value: 'assets' },
])
const activeTab = ref(tabButtonData.value[0])

// Chart data from retirement calculator store
const chartData = computed(() => retirementCalculatorStore.getGraphData)
const loading = computed(() => retirementCalculatorStore.getGraphLoading)
const userData = computed(() => retirementCalculatorStore.getUserData)
const partnerData = computed(() => retirementCalculatorStore.getPartnerData)
const enablePartnerForm = computed(() => retirementCalculatorStore.getEnablePartnerForm)

// Chart data for IncomeProjectionChart component (legacy format)
const assetChartData = computed(() => processedAssetData.value.chartData)

const incomeChartData = computed(() => {
  try {
    if (!chartData.value?.income?.years || typeof chartData.value.income.years !== 'object') {
      return []
    }

    const chartJsData = chartProcessor.mapIncomeData(
      chartData.value.income.years,
      userData.value,
      partnerData.value,
      enablePartnerForm.value,
    )

    // Ensure result has proper structure
    if (!chartJsData || !Array.isArray(chartJsData.datasets)) {
      console.warn('Income processor returned invalid structure:', chartJsData)
      return []
    }

    // Convert to IncomeProjectionChart format
    return chartProcessor.convertToIncomeProjectionFormat(chartJsData)
  } catch (error) {
    console.error('Error processing income chart data:', error)
    return []
  }
})

// Raw data for IncomeProjectionChart tooltips and annotations
const incomeChartRaw = computed(() => {
  try {
    if (!chartData.value?.income?.years || typeof chartData.value.income.years !== 'object') {
      return {}
    }

    return chartProcessor.generateRawDataFromIncome(chartData.value.income.years, userData.value)
  } catch (error) {
    console.error('Error generating income chart raw data:', error)
    return {}
  }
})

const assetChartRaw = computed(() => {
  try {
    if (!chartData.value?.asset || !Array.isArray(chartData.value.asset)) {
      console.log('No asset data found in chartData.value:', chartData.value)
      return {}
    }

    return chartProcessor.generateRawDataFromAssets(chartData.value.asset, userData.value)
  } catch (error) {
    console.error('Error generating asset chart raw data:', error)
    return {}
  }
})

// Chart metadata matching IncomeProjectionUserMetadata interface
const userMetadata = computed(() => ({
  userFirstName: userData.value.first_name || 'User',
  partnerFirstName: enablePartnerForm.value ? partnerData.value.first_name || 'Partner' : undefined,
  userRetirementAge: userData.value.desired_retirement_age || 67,
  partnerRetirementAge: enablePartnerForm.value
    ? partnerData.value.desired_retirement_age || 67
    : undefined,
}))

// Computed property for processed asset chart data with labels
const processedAssetData = computed(() => {
  try {
    if (!chartData.value?.asset || !Array.isArray(chartData.value.asset)) {
      return { chartData: [], labels: [] }
    }

    const chartJsData = chartProcessor.mapAllRetCalcAssetsData(chartData.value, userData.value)

    // Ensure result has proper structure
    if (!chartJsData || !Array.isArray(chartJsData.datasets)) {
      console.warn('Asset processor returned invalid structure:', chartJsData)
      return { chartData: [], labels: [] }
    }

    // Convert to IncomeProjectionChart format
    return {
      chartData: chartProcessor.convertToAssetsProjectionFormat(chartJsData),
      labels: chartJsData.labels || [],
    }
  } catch (error) {
    console.error('Error processing asset chart data:', error)
    return { chartData: [], labels: [] }
  }
})

const chartLabels = computed(() => processedAssetData.value.labels)

const desiredRetirementIncome = computed(() => userData.value.desired_retirement_income || 0)
</script>
